import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Mail, Instagram, Linkedin, Youtube } from "lucide-react";

const CONTACT_FAQ: { q: string; a: string }[] = [
  {
    q: "What is Raj Shamani's official email address?",
    a: "The official email for Raj Shamani and the Figuring Out team is partnerships@figuringout.co. It is monitored for brand partnerships, speaking engagements, podcast guest requests and media enquiries.",
  },
  {
    q: "How do I contact Raj Shamani for a brand collaboration or sponsorship?",
    a: "Email partnerships@figuringout.co or use the contact form on this page. Include your brand name, campaign objective, timeline and budget range so the team can respond faster.",
  },
  {
    q: "How can I invite Raj Shamani as a speaker at my event?",
    a: "Send the event date, city, audience size, format and topic through the contact form on this page or to partnerships@figuringout.co. Speaking requests are reviewed by the team on a first-come basis.",
  },
  {
    q: "How do I apply to be a guest on the Figuring Out podcast?",
    a: "Guest applications go through the Be a Guest page on rajshamani.life, which collects your background, story and the topics you can speak about. Applications sent to the general inbox are redirected there.",
  },
  {
    q: "Where can I follow Raj Shamani on social media?",
    a: "Raj Shamani is active as @rajshamani on Instagram, Raj Shamani on LinkedIn and @RajShamani on YouTube, where the Figuring Out podcast is published.",
  },
  {
    q: "How long does it take to get a reply?",
    a: "The team reads every message and typically replies to relevant business, partnership and media enquiries within 5 to 7 working days.",
  },
];

export const Route = createFileRoute("/contact")({

  head: () => ({
    meta: [
      { title: "Contact Raj Shamani — Email, Bookings & Partnerships", key: "title" },
      { name: "description", content: "Official contact page for Raj Shamani. Email partnerships@figuringout.co for brand deals, speaking, podcast guest requests and media enquiries.", key: "description" },
      { property: "og:title", content: "Contact Raj Shamani — Email, Bookings & Partnerships", key: "og:title" },
      { property: "og:description", content: "Official contact page for Raj Shamani. Email partnerships@figuringout.co for brand deals, speaking, podcast guest requests and media enquiries.", key: "og:description" },
      { property: "og:url", content: "https://rajshamani.life/contact", key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      { property: "og:site_name", content: "Raj Shamani", key: "og:site_name" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: "Contact Raj Shamani — Email, Bookings & Partnerships", key: "twitter:title" },
      { name: "twitter:description", content: "Official contact page for Raj Shamani. Email partnerships@figuringout.co for brand deals, speaking, podcast guest requests and media enquiries.", key: "twitter:description" },
    ],
    links: [{ rel: "canonical", href: "https://rajshamani.life/contact" }],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "@id": "https://rajshamani.life/contact#webpage",
          url: "https://rajshamani.life/contact",
          name: "Contact Raj Shamani — Email, Bookings & Partnerships",
          description:
            "Official contact page for Raj Shamani — brand partnerships, speaking engagements, podcast guest requests and media enquiries.",
          inLanguage: "en-IN",
          isPartOf: { "@id": "https://rajshamani.life/#website" },
          about: { "@id": "https://rajshamani.life/#organization" },
          mainEntity: {
            "@type": "Organization",
            "@id": "https://rajshamani.life/#organization",
            name: "Raj Shamani",
            url: "https://rajshamani.life/",
            contactPoint: [
              {
                "@type": "ContactPoint",
                contactType: "partnerships",
                email: "partnerships@figuringout.co",
                areaServed: "IN",
                availableLanguage: ["en", "hi"],
              },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://rajshamani.life/contact#faq",
          mainEntity: CONTACT_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],


  }),
  component: Contact,
});

const schema = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.string().trim().email().max(255),
  subject: z.string().trim().max(200).optional(),
  message: z.string().trim().min(10).max(2000),
});

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const messageId = useId();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? "Please fill all fields correctly.");
      return;
    }
    setLoading(true);
    let ok = false;
    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      ok = res.ok;
    } catch {
      ok = false;
    }
    setLoading(false);
    if (!ok) {
      toast.error("Something went wrong. Try again.");
      return;
    }
    toast.success("Message sent. Check your inbox — we've sent you something.");
    setForm({ name: "", email: "", subject: "", message: "" });

  }

  return (
    <div>
      <PageBreadcrumb current="Contact" />
      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4">Contact</div>
          <h1 className="text-5xl md:text-6xl font-bold">Contact <span className="text-gradient-gold">Raj Shamani</span></h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            This is the official contact page for Raj Shamani — entrepreneur, investor and
            host of the Figuring Out podcast. Use it for brand partnerships, sponsorships,
            speaking engagements, podcast guest requests and media enquiries.
          </p>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            The fastest route is email:{" "}
            <a href="mailto:partnerships@figuringout.co" className="text-gold hover:underline">
              partnerships@figuringout.co
            </a>
            . The team reads every message and usually replies to relevant enquiries within
            5–7 working days.
          </p>

          <div className="mt-10 p-6 md:p-8 rounded-sm border border-border bg-background/60">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-3">Aion</div>
            <h2 className="font-display text-xl md:text-2xl font-semibold leading-snug">
              Enquiring about Aion, Raj Shamani's AI course?
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              For course access, enrolment and support questions about Aion, start on the
              Aion page — it covers the curriculum, pricing and how to join.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/aion"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-gold text-primary-foreground font-medium hover:opacity-90 transition"
              >
                Explore Aion
              </Link>
              <a
                href="https://aion.engineer"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-border text-foreground hover:border-gold/60 hover:text-gold transition"
              >
                Visit aion.engineer
              </a>
            </div>
          </div>
        </div>

      </section>

      <section className="mx-auto max-w-5xl px-6 py-24 grid md:grid-cols-[1fr_320px] gap-12">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold mb-8">Send a message</h2>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
            </div>
            <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
            <div>
              <label htmlFor={messageId} className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Message</label>
              <textarea
                id={messageId}
                required
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 rounded-md bg-input border border-border focus:outline-none focus:ring-1 focus:ring-gold resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 rounded-md bg-gold text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>

        <aside className="space-y-6">
          <h2 className="text-2xl md:text-3xl font-bold">Connect with us</h2>
          <div className="p-6 rounded-xl bg-card border border-border">
            <h3 className="font-bold mb-4 text-gradient-gold">Quick Links</h3>
            <ul className="space-y-3 text-sm">
              <li><a href="mailto:partnerships@figuringout.co" className="flex items-center gap-3 text-muted-foreground hover:text-gold"><Mail size={16} /> partnerships@figuringout.co</a></li>
              <li><a href="https://instagram.com/rajshamani" target="_blank" rel="noopener" className="flex items-center gap-3 text-muted-foreground hover:text-gold"><Instagram size={16} /> @rajshamani</a></li>
              <li><a href="https://linkedin.com/in/rajshamani" target="_blank" rel="noopener" className="flex items-center gap-3 text-muted-foreground hover:text-gold"><Linkedin size={16} /> Raj Shamani</a></li>
              <li><a href="https://youtube.com/@RajShamani" target="_blank" rel="noopener" className="flex items-center gap-3 text-muted-foreground hover:text-gold"><Youtube size={16} /> @RajShamani</a></li>
            </ul>
          </div>
        </aside>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-24">
        <h2 className="text-2xl md:text-3xl font-bold mb-8">Contact Raj Shamani — FAQs</h2>
        <div className="space-y-6">
          {CONTACT_FAQ.map((f) => (
            <div key={f.q} className="p-6 rounded-xl bg-card border border-border">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 p-6 rounded-xl bg-card border border-border">
          <h2 className="text-xl font-bold mb-4">Before you write</h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>
              Want to be on the podcast?{" "}
              <Link to="/guest" className="text-gold hover:underline">Apply on the Be a Guest page</Link>.
            </li>
            <li>
              Curious about background, age or net worth?{" "}
              <Link to="/about" className="text-gold hover:underline">Read the About page</Link> or the{" "}
              <Link to="/faq" className="text-gold hover:underline">FAQ</Link>.
            </li>
            <li>
              Looking for episodes?{" "}
              <Link to="/podcast" className="text-gold hover:underline">Browse Figuring Out episodes</Link>.
            </li>
            <li>
              Business and investment info is on the{" "}
              <Link to="/ventures" className="text-gold hover:underline">Ventures page</Link>.
            </li>
          </ul>
        </div>
      </section>
    </div>

  );
}

function Field({ label, value, onChange, type = "text", required = false }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">{label}</label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-md bg-input border border-border focus:outline-none focus:ring-1 focus:ring-gold"
      />
    </div>
  );
}
